import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { Box, Typography, Avatar, Chip, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { PageHeader } from '../components/shared/PageHeader'
import { PageActionButtons } from '../components/shared/PageActionButtons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { themeColors } from '../app/layout/theme'
import { useOrgFilter, useOrgSelector } from '../context/OrgSelectorContext'
import { dealsApi } from '../lib/api/dealsApi'
import { salesApi, SalesRecord } from '../lib/api/salesApi'
import { orgsApi, lobsApi } from '../lib/api/tenantsApi'
import { authStore } from '../lib/stores/authStore'
import { observer } from 'mobx-react-lite'
import { ListToolbar, type BulkAction } from '../components/shared/ListToolbar'
import { UnifiedFilters, EMPTY_UNIFIED_FILTERS, FilterDef, countActiveFilters, EMPTY_PRICE_FILTER } from '../components/shared/UnifiedFilterDialog'
import { getCustomerTypes, type CustomerType } from '../lib/api/customerTypesApi'
import {
  AttachMoney as RevenueIcon,
  TrendingUp as TrendIcon,
  CalendarToday as CalIcon,
  ShowChart as ChartIcon,
  TrendingUp as DealIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { DataTable, type Column } from '../components/ui/DataTable'
import { DeleteDialog } from '../components/shared/DeleteDialog'
import { FormSelect } from '../components/ui'
import { usersAdminApi, type AdminUser } from '../lib/api/usersAdminApi'
import { usePermission } from '../lib/hooks/usePermission'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatAmount(amount: number | null, currency: string) {
  if (amount == null) return '—'
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 0 }).format(amount)
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime()) || d.getFullYear() <= 1970) return '—'
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

// const AVATAR_COLORS = ['#0070d2', '#2e7d32', '#7b1fa2', '#f57c00', '#c62828', '#00838f']
function avatarColor(name: string) {
  // let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  // return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
  return themeColors.primary
}
function initials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}

export const fixedStatuses = ['New', 'Onboarded', 'Won', 'Lost'] as const

export const STATUS_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  New: { bg: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' },
  Onboarded: { bg: '#fff7ed', color: '#c2410c', border: '1px solid #ffedd5' },
  Won: { bg: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
  Lost: { bg: '#fef2f2', color: '#dc2626', border: '1px solid #fee2e2' },
}

export function getStatusStyle(status: string) {
  return STATUS_COLORS[status] || { bg: '#f3f4f6', color: '#4b5563', border: '1px solid #e5e7eb' }
}

const SalesPage = observer(function SalesPage() {
  const navigate = useNavigate()

  const [dealPage, setDealPage] = useState(1)
  const [dealPageSize, setDealPageSize] = useState(() => {
    const saved = localStorage.getItem('crm_sales_page_size')
    return saved ? Number(saved) : 10
  })
  const [dealSearch, setDealSearch] = useState('')
  const [filters, setFilters] = useState<UnifiedFilters>(() => {
    const saved = sessionStorage.getItem('crm-sales-filters')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        return { ...EMPTY_UNIFIED_FILTERS, ...parsed, price: parsed.price ?? EMPTY_PRICE_FILTER }
      } catch { }
    }
    return EMPTY_UNIFIED_FILTERS
  })

  useEffect(() => {
    sessionStorage.setItem('crm-sales-filters', JSON.stringify(filters))
  }, [filters])

  // SuperAdmin detection: OrgId/LobId-based (matches the navbar org selector), not
  // usePermission's roleName-string match — that doesn't reliably match every
  // SuperAdmin's role label, which previously hid the Organisation/LOB filters here.
  const { isSuperAdmin } = useOrgSelector()

  // Org Admin tier (mirrors backend ITenantContext convention): has an orgId but no
  // specific lobId — sees/filters across every LOB in their own org, unlike a Normal User.
  const userOrgId  = authStore.user?.orgId ?? ''
  const userLobId  = authStore.user?.lobId ?? ''
  const isOrgAdmin = !isSuperAdmin && !!userOrgId && !userLobId

  const navOrgFilter = useOrgFilter()

  // Local page filter takes precedence; navbar org filter is the fallback for SuperAdmin
  const effectiveOrgIds = isSuperAdmin && filters.checkboxes.orgId?.length
    ? filters.checkboxes.orgId
    : navOrgFilter.orgId ? [navOrgFilter.orgId] : undefined

  // Restore the effectiveOrgId for the query:
  const effectiveOrgId = (isSuperAdmin && filters.checkboxes.orgId?.[0]) ? filters.checkboxes.orgId[0] : navOrgFilter.orgId

  const { data: orgs = [] } = useQuery({ queryKey: ['admin-orgs'], queryFn: orgsApi.getAll, enabled: isSuperAdmin, staleTime: 60_000 })
  // Prefer the org picked in this page's own filter dialog; fall back to whatever
  // org is selected in the navbar so the LOB list works right after picking an org
  // up top, without needing to also set the local Organisation filter.
  const orgIdForFetching = filters.checkboxes.orgId?.[0] || navOrgFilter.orgId
  // SuperAdmin: LOBs depend on whichever org they've selected in the filter.
  // Org Admin: LOBs are always scoped to their own (fixed) org — no org selector needed.
  const lobOrgIdForFetching = isSuperAdmin ? orgIdForFetching : userOrgId
  const { data: lobsForFilter = [] } = useQuery({
    queryKey: ['admin-lobs', lobOrgIdForFetching],
    queryFn: () => lobsApi.getAll(lobOrgIdForFetching!),
    enabled: (isSuperAdmin || isOrgAdmin) && !!lobOrgIdForFetching,
    staleTime: 30_000,
  })

  // 1. Fetch Revenue Summary from dedicated API (no record-count limit)
  // 1a. Params WITH user's date filter → Total Sales card only
  const revenueParams = useMemo(() => ({
    search: dealSearch || undefined,
    stages: filters.checkboxes.stage || undefined,
    ownerIds: filters.ownerIds?.length ? filters.ownerIds : undefined,
    assignedToIds: filters.checkboxes.userId?.length ? filters.checkboxes.userId : undefined,
    orgIds: effectiveOrgIds,
    lobIds: (isSuperAdmin || isOrgAdmin) && filters.checkboxes.lobId?.length ? filters.checkboxes.lobId : undefined,
    warehouseIds: isSuperAdmin && filters.checkboxes.warehouseId?.length ? filters.checkboxes.warehouseId : undefined,
    customerTypeIds: filters.checkboxes.customerTypeId?.length ? filters.checkboxes.customerTypeId.map(Number) : undefined,
    fromDate: filters.dateFrom ? `${filters.dateFrom}T00:00:00Z` : undefined,
    toDate: filters.dateTo ? `${filters.dateTo}T23:59:59Z` : undefined,
    // Price filters
    amountLt:  filters.price?.operator === 'lt'    && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountGt:  filters.price?.operator === 'gt'    && filters.price.value1 ? parseFloat(filters.price.value1)
             : filters.price?.operator === 'lt_gt' && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountEq:  filters.price?.operator === 'eq'    && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountLte: filters.price?.operator === 'lt_eq' && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountGte: filters.price?.operator === 'gt_eq' && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    ...(filters.price?.operator === 'lt_gt' && filters.price.value2 ? { amountLt: parseFloat(filters.price.value2) } : {}),
  }), [
    filters,
    dealSearch,
    effectiveOrgIds,
    isSuperAdmin,
    isOrgAdmin,
  ])

  // 1b. Params WITHOUT dates → Monthly / Quarterly / Yearly KPI cards
  // Backend uses DateTime.UtcNow to compute current-period buckets when no date is passed
  const kpiRevenueParams = useMemo(() => ({
    search: dealSearch || undefined,
    stages: filters.checkboxes.stage || undefined,
    ownerIds: filters.ownerIds?.length ? filters.ownerIds : undefined,
    assignedToIds: filters.checkboxes.userId?.length ? filters.checkboxes.userId : undefined,
    orgIds: effectiveOrgIds,
    lobIds: (isSuperAdmin || isOrgAdmin) && filters.checkboxes.lobId?.length ? filters.checkboxes.lobId : undefined,
    warehouseIds: isSuperAdmin && filters.checkboxes.warehouseId?.length ? filters.checkboxes.warehouseId : undefined,
    customerTypeIds: filters.checkboxes.customerTypeId?.length ? filters.checkboxes.customerTypeId.map(Number) : undefined,
    // Price filters
    amountLt:  filters.price?.operator === 'lt'    && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountGt:  filters.price?.operator === 'gt'    && filters.price.value1 ? parseFloat(filters.price.value1)
             : filters.price?.operator === 'lt_gt' && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountEq:  filters.price?.operator === 'eq'    && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountLte: filters.price?.operator === 'lt_eq' && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountGte: filters.price?.operator === 'gt_eq' && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    ...(filters.price?.operator === 'lt_gt' && filters.price.value2 ? { amountLt: parseFloat(filters.price.value2) } : {}),
  }), [
    filters,
    dealSearch,
    effectiveOrgIds,
    isSuperAdmin,
    isOrgAdmin,
  ])

  // Total Sales — respects user's selected date range
  const { data: salesRevenue } = useQuery({
    queryKey: ['sales-revenue', revenueParams],
    queryFn: () => salesApi.getRevenue(revenueParams),
    staleTime: 30_000,
  })

  // Monthly / Quarterly / Yearly — always current calendar period (no date filter)
  const { data: kpiRevenue } = useQuery({
    queryKey: ['sales-revenue-kpi', kpiRevenueParams],
    queryFn: () => salesApi.getRevenue(kpiRevenueParams),
    staleTime: 5 * 60_000, // KPIs are stable — cache for 5 minutes
  })

  const totalSales       = salesRevenue?.totalRevenue     ?? 0  // filtered by user's date range
  const monthlyRevenue   = kpiRevenue?.monthlyRevenue     ?? 0  // always this calendar month
  const quarterlyRevenue = kpiRevenue?.quarterlyRevenue   ?? 0  // always this quarter
  const yearlyRevenue    = kpiRevenue?.yearlyRevenue      ?? 0  // always this year



  const fmtCurrency = (n: number) => {
    if (n === 0) return '₹0'
    if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(n % 1_00_00_000 === 0 ? 0 : 2)}Cr`
    if (n >= 1_00_000)    return `₹${(n / 1_00_000).toFixed(n % 1_00_000 === 0 ? 0 : 2)}L`
    return formatAmount(n, 'INR')
  }

  const headerStats = [
    {
      label: 'Total Sales',
      value: fmtCurrency(totalSales),
      icon: <RevenueIcon sx={{ fontSize: 18 }} />,
      color: '#059669',
    },
    {
      label: 'Monthly Revenue',
      value: fmtCurrency(monthlyRevenue),
      icon: <CalIcon sx={{ fontSize: 18 }} />,
      color: themeColors.primary,
    },
    {
      label: 'Quarterly Revenue',
      value: fmtCurrency(quarterlyRevenue),
      icon: <ChartIcon sx={{ fontSize: 18 }} />,
      color: themeColors.warning,
    },
    {
      label: 'Yearly Revenue',
      value: fmtCurrency(yearlyRevenue),
      icon: <TrendIcon sx={{ fontSize: 18 }} />,
      color: themeColors.success,
    }
  ]

  const { data: usersData } = useQuery({
    queryKey: ['users-for-assign'],
    queryFn: () => usersAdminApi.getAll({ pageSize: 1000 }),
    staleTime: 60_000,
  })
  const usersList = usersData?.items ?? []

  // 2. Fetch Paginated Sales Records for Table from actual sales table
  const salesParams = {
    search: dealSearch || undefined,
    stages: filters.checkboxes.stage || undefined,
    ownerIds: filters.ownerIds?.length ? filters.ownerIds : undefined,
    assignedToIds: filters.checkboxes.userId?.length ? filters.checkboxes.userId : undefined,
    orgIds: effectiveOrgIds,
    lobIds: (isSuperAdmin || isOrgAdmin) && filters.checkboxes.lobId?.length ? filters.checkboxes.lobId : undefined,
    warehouseIds: isSuperAdmin && filters.checkboxes.warehouseId?.length ? filters.checkboxes.warehouseId : undefined,
    customerTypeIds: filters.checkboxes.customerTypeId?.length ? filters.checkboxes.customerTypeId.map(Number) : undefined,
    fromDate: filters.dateFrom ? `${filters.dateFrom}T00:00:00Z` : undefined,
    toDate: filters.dateTo ? `${filters.dateTo}T23:59:59Z` : undefined,
    // Price filters
    amountLt:  filters.price?.operator === 'lt'    && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountGt:  filters.price?.operator === 'gt'    && filters.price.value1 ? parseFloat(filters.price.value1)
             : filters.price?.operator === 'lt_gt' && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountEq:  filters.price?.operator === 'eq'    && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountLte: filters.price?.operator === 'lt_eq' && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountGte: filters.price?.operator === 'gt_eq' && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    ...(filters.price?.operator === 'lt_gt' && filters.price.value2 ? { amountLt: parseFloat(filters.price.value2) } : {}),
    page: dealPage,
    pageSize: dealPageSize,
  }

  const { data: salesListData, isLoading: salesLoading } = useQuery({
    queryKey: ['sales-list-records', salesParams],
    queryFn: () => salesApi.getAll(salesParams),
    staleTime: 30_000,
  })

  const salesList = salesListData?.items ?? []
  const totalSalesCount = salesListData?.total ?? 0
  const totalSalesPages = salesListData?.pageSize ? Math.ceil((salesListData?.total ?? 0) / salesListData.pageSize) : 1

  const { data: customerTypes = [] } = useQuery<CustomerType[]>({
    queryKey: ['customer-types'],
    queryFn: getCustomerTypes,
    staleTime: 5 * 60 * 1000,
  })

  const filterDefs: FilterDef[] = useMemo(() => {
    const defs: FilterDef[] = [
      {
        type: 'checkbox',
        key: 'stage',
        label: 'Stage',
        options: fixedStatuses.map(s => ({ label: s, value: s })),
      },
      {
        type: 'checkbox',
        key: 'customerTypeId',
        label: 'Customer Type',
        options: customerTypes.map(ct => ({ label: ct.name, value: String(ct.id) })),
      },
      { type: 'price', label: 'Price' },
      { type: 'date', label: 'Date Created' },
    ]

    if (authStore.canViewLobWideData) {
      defs.push(
        {
          type: 'checkbox',
          key: 'userId',
          label: 'Assignee',
          options: [
            { label: 'Unassigned', value: 'unassigned' },
            ...usersList.map(u => ({ label: u.name, value: u.id })),
          ],
        },
        { type: 'owner', label: 'Owner' },
      )
    }

    if (isSuperAdmin) {
      defs.push(
        {
          type: 'checkbox',
          key: 'orgId',
          label: 'Organisation',
          options: orgs.map(o => ({ label: o.name, value: o.id })),
        },
        {
          type: 'checkbox',
          key: 'lobId',
          label: 'LOB',
          options: lobsForFilter.map(l => ({ label: l.name, value: l.id })),
        },
      )
    } else if (isOrgAdmin) {
      // Org Admin's org is fixed (no Organisation picker needed) — just let them
      // narrow the list down to one or more LOBs within their own org.
      defs.push({
        type: 'checkbox',
        key: 'lobId',
        label: 'LOB',
        options: lobsForFilter.map(l => ({ label: l.name, value: l.id })),
      })
    }

    return defs
  }, [fixedStatuses, customerTypes, isSuperAdmin, isOrgAdmin, orgs, lobsForFilter, usersList])

  const salesColumns: Column[] = [
    { id: 'dealName', label: 'Deal', sortable: true, render: (_, record: SalesRecord) => record.dealName },
    { id: 'company', label: 'Company', sortable: true, render: (_, record: SalesRecord) => record.company || '-' },
    { id: 'contact', label: 'Contact Name', sortable: true, render: (_, record: SalesRecord) => record.contactName || '-' },
    {
      id: 'value',
      label: 'Expected Value',
      sortable: true,
      render: (_, record: SalesRecord) => (
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
          {formatAmount(record.value, 'INR')}
        </Typography>
      ),
    },
    {
      id: 'actualValue',
      label: 'Actual Value',
      sortable: true,
      render: (_, record: SalesRecord) => {
        const orderValStr = record.onboard?.orderValue
        const parsedVal = orderValStr ? parseFloat(orderValStr.replace(/[^0-9.]/g, '')) : null
        return (
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: orderValStr ? '#059669' : '#94a3b8' }}>
            {orderValStr ? (!isNaN(parsedVal!) && parsedVal !== null ? formatAmount(parsedVal, 'INR') : orderValStr) : '—'}
          </Typography>
        )
      },
    },
    { id: 'closeDate', label: 'Won Date', sortable: true, render: (_, record: SalesRecord) => (record.status === 'Won' || record.closeDate ? formatDate(record.closeDate) : '—') },
    {
      id: 'owner',
      label: 'Owner',
      sortable: true,
      render: (_, record: SalesRecord) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Avatar sx={{ width: 21, height: 21, fontSize: '9px', fontWeight: 700, bgcolor: avatarColor(record.ownerName || 'Unknown') }}>
            {initials(record.ownerName || 'Unknown')}
          </Avatar>
          <Typography sx={{ fontSize: '12px', color: themeColors.text.primary, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {record.ownerName || 'Unknown'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'assignee',
      label: 'Assignee',
      sortable: true,
      render: (_, record: SalesRecord) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Avatar sx={{ width: 21, height: 21, fontSize: '9px', fontWeight: 700, bgcolor: record.assignedToName && record.assignedToName !== 'Unassigned' ? avatarColor(record.assignedToName) : '#94a3b8' }}>
            {record.assignedToName && record.assignedToName !== 'Unassigned' ? initials(record.assignedToName) : '?'}
          </Avatar>
          <Typography sx={{ fontSize: '12px', color: record.assignedToName && record.assignedToName !== 'Unassigned' ? themeColors.text.primary : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {record.assignedToName || 'Unassigned'}
          </Typography>
        </Box>
      ),
    },

    {
      id: 'status',
      label: 'Stage',
      sortable: true,
      render: (_, record: SalesRecord) => {
        const name = record.status || 'New'
        const style = getStatusStyle(name)
        return (
          <Chip
            label={name}
            size="small"
            sx={{
              height: 20,
              borderRadius: '4px',
              bgcolor: style.bg,
              color: style.color,
              border: style.border,
              fontSize: '11px',
              fontWeight: 600,
              '& .MuiChip-label': { px: '8px' }
            }}
          />
        )
      },
    },
  ]

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false)
  const [bulkAssignUser, setBulkAssignUser] = useState<AdminUser | null>(null)

  const { hasAction } = usePermission()
  const canAssign = hasAction('/sales', 'assign')

  const qc = useQueryClient()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['sales-list-records'] })
    qc.invalidateQueries({ queryKey: ['sales-deals'] })
    qc.invalidateQueries({ queryKey: ['sales-revenue'] })
    qc.invalidateQueries({ queryKey: ['sales-revenue-kpi'] })
  }


  const selectedSalesRecords = useMemo(() => salesList.filter(s => selected.has(s.id)), [salesList, selected])
  const openSalesInSelection = useMemo(() => selectedSalesRecords.filter(s => s.status !== 'Won' && s.status !== 'Lost'), [selectedSalesRecords])
  const closedSalesInSelection = useMemo(() => selectedSalesRecords.filter(s => s.status === 'Won' || s.status === 'Lost'), [selectedSalesRecords])

  const selectedOrgIds = useMemo(() => Array.from(new Set(selectedSalesRecords.map(s => s.orgId).filter(Boolean))), [selectedSalesRecords])
  const hasMultipleOrgsSelected = selectedOrgIds.length > 1
  const targetOrgId = selectedOrgIds.length === 1 ? selectedOrgIds[0] : null

  const selectedLobIds = useMemo(() => Array.from(new Set(selectedSalesRecords.map(s => s.lobId).filter(Boolean))), [selectedSalesRecords])
  const hasMultipleLobsSelected = selectedLobIds.length > 1
  const targetLobId = selectedLobIds.length === 1 ? selectedLobIds[0] : null

  const filteredUsersList = useMemo(() => {
    return usersList.filter(u => {
      if (u.roleName?.toUpperCase() === 'SUPER_ADMIN') return false
      if (targetOrgId && u.orgId !== targetOrgId) return false
      if (!u.lobId) return true // Org Admin — eligible across all LOBs in the org
      if (hasMultipleLobsSelected) return false // normal user can't cover multiple LOBs at once
      if (targetLobId && u.lobId !== targetLobId) return false
      return true
    })
  }, [usersList, targetOrgId, targetLobId, hasMultipleLobsSelected])

  const bulkAssignMutation = useMutation({
    mutationFn: async () => {
      if (!bulkAssignUser) return { count: 0, name: '' }
      const assignableSales = selectedSalesRecords.filter(s => s.status !== 'Won' && s.status !== 'Lost')
      if (assignableSales.length === 0) return { count: 0, name: '' }
      const name = bulkAssignUser.name
      await Promise.all(assignableSales.map(s => salesApi.reassign(s.id, bulkAssignUser.id)))
      return { count: assignableSales.length, name }
    },
    onSuccess: ({ count, name }) => {
      invalidate()
      setSelected(new Set())
      setBulkAssignOpen(false)
      setBulkAssignUser(null)
      toast.success(`${count} sale(s) reassigned to ${name}`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to reassign sales'),
  })

  const bulkActions: BulkAction[] = useMemo(() => {
    const actions: BulkAction[] = []
    if (canAssign) {
      actions.push({
        label: 'Assign to...',
        onClick: () => setBulkAssignOpen(true),
      })
    }
    return actions
  }, [canAssign])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Sales"
        breadcrumb="CRM"
        stats={headerStats}
        actions={
          <PageActionButtons
            onExport={async () => {
              await salesApi.export(salesParams)
            }}
          />
        }
      />

      {/* ── Pinned toolbar ── */}
      <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
        <ListToolbar
          search={dealSearch}
          onSearch={(v) => { setDealSearch(v); setDealPage(1) }}
          searchPlaceholder="Search deals..."
          filterDefs={filterDefs}
          filters={filters}
          onFilterChange={(f) => { setFilters(f); setDealPage(1) }}
          selectedCount={selected.size}
          bulkActions={bulkActions}
          onClearSelection={() => setSelected(new Set())}
        />
      </Box>

      {/* ── Scrollable content ── */}
      <Box sx={{ flex: 1, px: 3, pb: 3, pt: 2, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <DataTable
            columns={salesColumns}
            rows={salesList}
            isLoading={salesLoading}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            showRowNumbers={false}
            onRowClick={(record) => navigate(`/sales/${record.id}`)}
            emptyIcon={<DealIcon />}
            emptyMessage={dealSearch || countActiveFilters(filters) > 0 ? 'No deals match your filters' : 'No deals found'}
            emptyHint={dealSearch || countActiveFilters(filters) > 0 ? 'Try adjusting your filters' : 'Convert some leads to deals to get started'}
            pagination={{
              page: dealPage,
              pageSize: dealPageSize,
              total: totalSalesCount,
              totalPages: totalSalesPages,
              itemLabel: 'results',
              onPageChange: setDealPage,
              onPageSizeChange: (s) => { setDealPageSize(s); localStorage.setItem('crm_sales_page_size', String(s)); setDealPage(1) },
              pageSizeOptions: [10, 20, 50, 100],
            }}
          />
        </Box>
      </Box>

      {/* ── Dialogs ── */}

      <Dialog
        open={bulkAssignOpen}
        onClose={() => { setBulkAssignOpen(false); setBulkAssignUser(null) }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '16px' }}>
          Assign {openSalesInSelection.length} Selected Sale(s)
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {closedSalesInSelection.length > 0 && (
            <Typography fontSize="12.5px" color="warning.main" sx={{ pb: 1, fontWeight: 600 }}>
              ⚠️ {closedSalesInSelection.length} selected sale(s) are closed (Won/Lost) and cannot be reassigned. Only active sales will be reassigned.
            </Typography>
          )}
          {hasMultipleOrgsSelected ? (
            <Typography fontSize="13px" color="error" sx={{ py: 1, fontWeight: 500 }}>
              ⚠️ The selected sales belong to different organizations. Bulk assignment is only allowed for sales belonging to the same organization.
            </Typography>
          ) : (
            <>
              {hasMultipleLobsSelected && (
                <Typography fontSize="12px" color="text.secondary" sx={{ pb: 1 }}>
                  Selected sales span multiple LOBs — only Organization Admins are eligible for this bulk assignment.
                </Typography>
              )}
              <FormSelect
                label="Assign To User"
                value={bulkAssignUser?.id ?? ''}
                onChange={(val: any) => {
                  const user = filteredUsersList.find(u => u.id === val?.value)
                  setBulkAssignUser(user ?? null)
                }}
                options={filteredUsersList.map(u => ({ label: `${u.name} (${u.email})`, value: u.id }))}
                fullWidth
              />
              {filteredUsersList.length === 0 && (
                <Typography fontSize="12px" color="error" sx={{ pt: 1 }}>
                  No eligible users found. Only users in the same LOB, or Organization Admins, can be assigned.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => { setBulkAssignOpen(false); setBulkAssignUser(null) }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={hasMultipleOrgsSelected || openSalesInSelection.length === 0 || !bulkAssignUser || bulkAssignMutation.isPending}
            onClick={() => bulkAssignMutation.mutate()}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: themeColors.primary,
              '&:hover': { bgcolor: themeColors.primaryDark },
            }}
          >
            {bulkAssignMutation.isPending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Confirm Assignment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
})

export default SalesPage
